import { useCallback, useEffect, useId, useRef, useState } from "react";
import { AdminDatePicker } from "@/components/admin/AdminDatePicker";
import { IconCalendar, IconHelpCircle } from "@/components/icons";
import { useClickOutside } from "@/hooks/useClickOutside";
import {
  DATE_RANGE_PRESETS_MAIN,
  DATE_RANGE_PRESETS_OTHER,
  formatDateRangeTriggerLabel,
  resolveDateRangePreset,
  type DateRangePresetId,
} from "@/lib/dates/dateRange";
import {
  combineDisplayDateTime,
  displayDateToIso,
  isoToDisplayDate,
  isoToDisplayTime,
  maskDisplayTime,
} from "@/lib/dates/isoDate";

const timeClassName =
  "w-[4.75rem] shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-onest text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20";

type Draft = {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
};

function rangeToDraft(from: string, to: string): Draft {
  return {
    fromDate: isoToDisplayDate(from),
    fromTime: from ? isoToDisplayTime(from) : "",
    toDate: isoToDisplayDate(to),
    toTime: to ? isoToDisplayTime(to) : "",
  };
}

export function AdminDateRangePicker({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}) {
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"main" | "other">("main");
  const [presetLabel, setPresetLabel] = useState<string | undefined>();
  const [draft, setDraft] = useState<Draft>(() => rangeToDraft(from, to));

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open);

  useEffect(() => {
    if (!open) return;
    setDraft(rangeToDraft(from, to));
  }, [open, from, to]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const applyRange = (nextFrom: string, nextTo: string, label?: string) => {
    setPresetLabel(label);
    onChange({ from: nextFrom, to: nextTo });
  };

  const applyDraft = () => {
    const hasFrom = Boolean(draft.fromDate);
    const hasTo = Boolean(draft.toDate);
    if (!hasFrom && !hasTo) {
      applyRange("", "", "За все время");
      setOpen(false);
      return;
    }
    const nextFrom = hasFrom
      ? combineDisplayDateTime(draft.fromDate, draft.fromTime, {
          hours: 0,
          minutes: 0,
        })
      : "";
    const nextTo = hasTo
      ? combineDisplayDateTime(draft.toDate, draft.toTime, {
          hours: 23,
          minutes: 59,
        })
      : "";
    if ((hasFrom && !nextFrom) || (hasTo && !nextTo)) return;
    applyRange(nextFrom ?? "", nextTo ?? "");
    setOpen(false);
  };

  const applyPreset = (id: DateRangePresetId, label: string) => {
    const range = resolveDateRangePreset(id);
    const nextLabel = id === "all" ? "За все время" : label;
    setDraft(rangeToDraft(range.from, range.to));
    applyRange(range.from, range.to, nextLabel);
  };

  const updateDate = (key: "from" | "to", iso: string) => {
    setPresetLabel(undefined);
    setDraft((current) => {
      const dateKey = key === "from" ? "fromDate" : "toDate";
      const timeKey = key === "from" ? "fromTime" : "toTime";
      const fallbackTime = key === "from" ? "00:00" : "23:59";
      const nextDate = isoToDisplayDate(iso);
      return {
        ...current,
        [dateKey]: nextDate,
        [timeKey]: nextDate ? current[timeKey] || fallbackTime : "",
      };
    });
  };

  const presets =
    tab === "main" ? DATE_RANGE_PRESETS_MAIN : DATE_RANGE_PRESETS_OTHER;
  const triggerLabel = formatDateRangeTriggerLabel(from, to, presetLabel);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={titleId}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-8 max-w-[16rem] items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 font-onest text-xs font-medium text-slate-700 hover:bg-slate-200/80"
      >
        <IconCalendar size={14} className="shrink-0 text-slate-500" />
        <span className="truncate">{triggerLabel}</span>
      </button>

      {open ? (
        <div
          id={titleId}
          role="dialog"
          aria-labelledby={`${titleId}-label`}
          className="absolute left-0 z-40 mt-1.5 w-[21rem] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
        >
          <div className="mb-2 flex items-center gap-1.5">
            <p
              id={`${titleId}-label`}
              className="font-onest text-sm font-medium text-slate-800"
            >
              Выберите интервал
            </p>
            <span
              title="Фильтр по дате ответа. Пустой интервал — все ответы."
              className="inline-flex text-slate-400"
            >
              <IconHelpCircle size={14} />
            </span>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="w-7 shrink-0 font-onest text-sm text-slate-600">
                От:
              </span>
              <AdminDatePicker
                compact
                calendarLabel="Календарь даты от"
                value={displayDateToIso(draft.fromDate) ?? ""}
                onChange={(iso) => updateDate("from", iso)}
              />
              <input
                value={draft.fromTime}
                inputMode="numeric"
                autoComplete="off"
                placeholder="чч:мм"
                aria-label="Время от"
                onChange={(event) => {
                  setPresetLabel(undefined);
                  setDraft((current) => ({
                    ...current,
                    fromTime: maskDisplayTime(event.target.value),
                  }));
                }}
                className={timeClassName}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-7 shrink-0 font-onest text-sm text-slate-600">
                До:
              </span>
              <AdminDatePicker
                compact
                calendarLabel="Календарь даты до"
                value={displayDateToIso(draft.toDate) ?? ""}
                onChange={(iso) => updateDate("to", iso)}
              />
              <input
                value={draft.toTime}
                inputMode="numeric"
                autoComplete="off"
                placeholder="чч:мм"
                aria-label="Время до"
                onChange={(event) => {
                  setPresetLabel(undefined);
                  setDraft((current) => ({
                    ...current,
                    toTime: maskDisplayTime(event.target.value),
                  }));
                }}
                className={timeClassName}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={applyDraft}
            className="mt-2 w-full rounded-lg bg-slate-100 py-2 font-onest text-sm font-medium text-slate-700 hover:bg-slate-200/80"
          >
            Применить
          </button>

          <div
            role="tablist"
            aria-label="Пресеты интервала"
            className="mt-3 flex gap-4 border-b border-slate-200"
          >
            {(
              [
                { id: "main", label: "Основное" },
                { id: "other", label: "Другое" },
              ] as const
            ).map((item) => {
              const selected = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setTab(item.id)}
                  className={[
                    "relative -mb-px pb-2 font-onest text-sm transition-colors",
                    selected
                      ? "font-medium text-brand-600"
                      : "text-slate-500 hover:text-slate-800",
                  ].join(" ")}
                >
                  {item.label}
                  {selected ? (
                    <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-brand-500" />
                  ) : null}
                </button>
              );
            })}
          </div>

          <ul className="mt-1">
            {presets.map((preset) => (
              <li key={preset.id}>
                <button
                  type="button"
                  onClick={() => applyPreset(preset.id, preset.label)}
                  className={[
                    "w-full rounded-md px-0.5 py-1.5 text-left font-onest text-sm hover:bg-slate-50",
                    presetLabel === preset.label ||
                    (preset.id === "all" && presetLabel === "За все время")
                      ? "font-medium text-brand-700"
                      : "text-slate-800",
                  ].join(" ")}
                >
                  {preset.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
